import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../styles/dashboardTheme.css";
import authService from "../../../services/authService";

const readStoredUser = () => {
  const rawUser = localStorage.getItem("user");
  if (!rawUser) return null;
  try {
    return JSON.parse(rawUser);
  } catch (error) {
    console.error("No se pudo leer el usuario guardado:", error);
    return null;
  }
};

const mergeProfile = (base = {}, incoming = {}) => {
  const merged = { ...base };
  Object.entries(incoming).forEach(([key, value]) => {
    const isEmptyString = typeof value === "string" && value.trim() === "";
    if (value !== undefined && value !== null && !isEmptyString) {
      merged[key] = value;
    }
  });
  return merged;
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(true);

  const initials = useMemo(() => {
    if (!profile) return "?";
    const source = profile.nombre_completo || profile.username || "";
    return source.trim().charAt(0).toUpperCase() || "?";
  }, [profile]);

  const formatValue = (value, fallback = "No registrado") => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === "string" && value.trim() === "") return fallback;
    return value;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await authService.me();
        const serverUser = data?.user || data;
        if (serverUser) {
          setProfile((prev) => {
            const merged = mergeProfile(prev || {}, serverUser);
            localStorage.setItem("user", JSON.stringify(merged));
            return merged;
          });
        }
      } catch (error) {
        console.error("No se pudo cargar el perfil desde el servidor:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading && !profile) {
    return (
      <section className="dashboard-page container-fluid py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted mb-0">Cargando tus datos de perfil...</p>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="dashboard-page container-fluid py-5 text-center">
        <p className="text-muted mb-0">No se encontraron datos de perfil.</p>
      </section>
    );
  }

  const infoSections = [
    {
      title: "Datos basicos",
      items: [
        { label: "Nombre completo", value: profile.nombre_completo, icon: "bi-person" },
        { label: "Rol", value: profile.rol?.nombre, icon: "bi-shield-lock" },
        { label: "Usuario", value: profile.username, icon: "bi-at" },
        { label: "Correo", value: profile.email, icon: "bi-envelope" },
      ],
    },
    {
      title: "Contacto y estado",
      items: [
        { label: "Documento de identidad", value: profile.documento_identidad, icon: "bi-credit-card-2-front" },
        { label: "Telefono", value: profile.telefono, icon: "bi-telephone" },
        {
          label: "Estado",
          value: profile.activo === false || profile.activo === 0 ? "Inactivo" : "Activo",
          icon: "bi-toggle-on",
        },
      ],
    },
  ];

  const handleBack = () => {
    const canGoBack = typeof window !== "undefined" && window.history.length > 2;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate("/dashboard/view_dashboard");
    }
  };

  return (
    <section className="dashboard-page container-fluid py-4">
      <div className="d-flex justify-content-end mb-2">
        <button
          type="button"
          className="btn btn-light d-inline-flex align-items-center gap-2"
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left"></i>
          Volver
        </button>
      </div>

      <div className="card border-0 shadow-sm dashboard-hero mb-4">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: "linear-gradient(135deg, #0ea5e9, #0d9488)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "1.75rem",
              }}
            >
              {initials}
            </div>
            <div>
              <p className="dashboard-eyebrow mb-1">Perfil</p>
              <h4 className="dashboard-title mb-0">
                {formatValue(profile.nombre_completo, "Usuario sin nombre")}
              </h4>
              <small className="text-muted">
                {formatValue(profile.rol?.nombre, "Rol no asignado")}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {infoSections.map((section) => (
          <div key={section.title} className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <p className="dashboard-label mb-3">{section.title}</p>
                <div className="row g-3">
                  {section.items.map(({ label, value, icon }) => (
                    <div key={label} className="col-12">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-3"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: 42,
                            height: 42,
                            background: "rgba(13, 148, 136, 0.12)",
                            color: "#0d9488",
                          }}
                        >
                          <i className={`bi ${icon}`}></i>
                        </div>
                        <div>
                          <div className="text-muted small">{label}</div>
                          <div className="fw-semibold">
                            {formatValue(value)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
